import {
	createContext,
	PropsWithChildren,
	useContext,
	FC,
	useRef,
	useEffect,
	useCallback,
	RefObject
} from 'react';
import { ExperimentClient } from '@amplitude/experiment-js-client/dist/types/src/experimentClient';
import * as amplitude from '@amplitude/analytics-browser';
import { getUserDataToWriteIntoAnalytics, UserDto } from '@sweatpals/shared';
import { Experiment } from '@amplitude/experiment-js-client';

export interface AmplitudeContextShape {
	isInitializedRef?: RefObject<boolean>;
	latestExperimentsFetch?: RefObject<Date>;
	experimentClientRef?: RefObject<ExperimentClient | undefined>;
	setUserIdExternally?: (userId: string) => void;
	currentUser?: UserDto | null;
	currentCommunity?: UserDto | null;
}

const AmplitudeContext = createContext<AmplitudeContextShape>({});

export const useAmplitudeContext = () => useContext(AmplitudeContext);

export type AmplitudeContextProviderProps = PropsWithChildren & {
	currentUser?: UserDto | null;
	currentCommunity?: UserDto | null;
}

/**
 * AmplitudeContext provides a centralized way to handle Amplitude analytics and experiment feature flags
 * throughout the application. This context makes logging and feature-flags accessible from any part
 * of any app without needing to redeclare hooks and contexts.
 *
 * @description The context handles initialization of Amplitude analytics, setting up experiment clients,
 * managing user identification, and fetching experiment data. It automatically updates when the current
 * user changes and provides methods to set user IDs externally.
 *
 * @example
 * // Wrap your application with the provider and pass the current user and community
 * <AmplitudeContextProvider currentUser={user}>
 *   <App />
 * </AmplitudeContextProvider>
 *
 * @note
 * Note: This context enables the use of hook utilities like use-amplitude-analytics
 * or use-amplitude-feature-flag anywhere in your component tree of any app. These hooks can be
 * used in any component that's wrapped with this provider.
 */
export const AmplitudeContextProvider: FC<AmplitudeContextProviderProps> = ({ children, currentUser, currentCommunity }) => {
	const isInitializedRef = useRef<boolean>(false);
	const experimentClientRef = useRef<ExperimentClient>();
	const latestExperimentsFetch = useRef<Date>(new Date());

	useEffect(() => {
		if (typeof window === undefined || isInitializedRef.current)
			return;

		const amplitudeApiKey = process.env['NEXT_PUBLIC_AMPLITUDE_API_KEY'] ?? process.env['NX_PUBLIC_AMPLITUDE_API_KEY'];

		if (!amplitudeApiKey) {
			return;
		}

		if (!isInitializedRef?.current && amplitudeApiKey) {
			amplitude.init(amplitudeApiKey, {
				appVersion: process.env['NEXT_PUBLIC_SENTRY_RELEASE'] ?? process.env['NX_PUBLIC_SENTRY_RELEASE'],
				autocapture: true,
			});

			console.info('[Amplitude] initialized');
		}

		if (!experimentClientRef?.current) {
			experimentClientRef.current = Experiment.initializeWithAmplitudeAnalytics(amplitudeApiKey);
			fetchExperimentsData().catch((e) => {
				console.error('[Amplitude_Exp] Error while fetching initial experiments data', e);
			});
		}

		isInitializedRef.current = true;
	}, [isInitializedRef.current]);

	const setCurrentUser = useCallback((currentUser?: UserDto | null, currentCommunity?: UserDto | null) => {
		try {
			amplitude.setUserId(currentUser?.id);

			if (currentUser?.id) {
				const identify = new amplitude.Identify();

				const analyticsUser = getUserDataToWriteIntoAnalytics(currentUser);

				for (const [key, value] of Object.entries(analyticsUser)) {
					if (key !== 'id') {
						// Amplitude actually takes objects as user properties just fine, and separates them with dot
						identify.set(key, value as string | boolean | number);
					}
				}

				if (currentCommunity) {
					identify.set('currentCommunityId', currentCommunity.id);
					identify.set('currentCommunityName', currentCommunity.fullName);
					identify.set('currentCommunityAlias', currentCommunity.userName);
					console.info('[Amplitude] Current community set: ', currentCommunity.fullName, currentCommunity.userName);
				} else {
					identify.unset('currentCommunityId');
					identify.unset('currentCommunityName');
					identify.unset('currentCommunityAlias');
					console.info('[Amplitude] Current community unset');
				}

				amplitude.identify(identify);
			} else {
				amplitude.identify(new amplitude.Identify());
			}

			console.info('[Amplitude] User set: ', currentUser?.fullName);
		} catch (e) {
			console.error('[Amplitude] Error while setting user id', e);
		}
	}, []);

	const fetchExperimentsData = useCallback(async () => {
		if (experimentClientRef?.current) {
			await experimentClientRef.current.fetch();
			latestExperimentsFetch.current = new Date()
		}
	}, [latestExperimentsFetch, experimentClientRef]);

	useEffect(() => {
		fetchExperimentsData().then(() => {
			console.info('[Amplitude_Exp] refetched for user: ', currentUser?.fullName);
		}).catch((e) => {
			console.error('[Amplitude_Exp] Error while fetching experiments data with user', e);
		});
	}, [currentUser?.id]);

	useEffect(() => {
		setCurrentUser(currentUser, currentCommunity);
	}, [setCurrentUser, currentUser?.id, currentCommunity?.id]);

	const setUserIdExternally = useCallback((userId: string) => {
		try {
			amplitude.setUserId(userId);
			console.info('[Amplitude] User id set: ', userId);

			fetchExperimentsData().catch((e) => {
				console.error('[Amplitude] Error while fetching experiments data with user', e);
			});
		} catch (e) {
			console.error('[Amplitude] Error while setting user id', e);
		}
	}, [fetchExperimentsData]);

	return (
		<AmplitudeContext.Provider value={{ isInitializedRef, experimentClientRef, latestExperimentsFetch, setUserIdExternally, currentUser, currentCommunity }}>
			{children}
		</AmplitudeContext.Provider>
	);
};
